import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import DataTable from '../../components/DataTable';
import DialogShell from '../../components/DialogShell';
import {
  listAccounts,
  getAccount,
  deleteAccount,
  addAccounts,
  testProvider,
  resetProvider,
  createDeletingRequest,
  cancelDeletingRequest,
  confirmDeletingRequest,
} from '../../services/cloneProvider';
import {
  LoginStateBadge,
  WorkingStateBadge,
  LiveDot,
  getErrorMessage,
  formatDateTime,
  formatVnd,
  formatNumber,
  VerticalStackedBar,
  ProviderStatsByServing,
  LOGIN_STATE_BAR_COLOR,
} from './badges';
import useUrlState, { useUrlStateBatch } from './useUrlState';

const LOGIN_STATES = [
  'live', 'logged_out', 'fun_captcha', 'recaptcha', 'text_captcha', 'unknown_captcha',
  'verify_code_via_email', 'verify_code_2fa', 'verify_other_device', 'invalid_request',
  'wrong_pass', 'cp956', 'cp282', 'disabled', 'unknown',
];
const SERVICE_TYPES = ['fb_ql', 'fb_eaaaau', 'fb_eaagno'];
const SERVICE_TYPE_LABEL = { fb_ql: 'FB QL', fb_eaaaau: 'FB EAAAAU', fb_eaagno: 'FB EAAGNO' };
const PAGE_SIZE = 50;

// 5 bar thống kê dùng chung giữa AccountsTab (theo filter hiện tại) và
// BulkDeleteDialog (theo đúng danh sách sẽ bị xoá) - cùng shape
// accounts_stats/providers_stats mà backend trả (manager.get_accounts_stats()/
// get_providers_stats()).
function AccountStatsBars({ accountsStats, providersStats }) {
  const loginStateSegments = accountsStats
    ? Object.entries(accountsStats.by_login_state).map(([state, count]) => ({
        key: state,
        count,
        color: LOGIN_STATE_BAR_COLOR[state] || '#94a3b8',
      }))
    : [];

  return (
    <div className="flex flex-wrap items-start gap-6 rounded-xl border border-cyan-100 bg-white p-3">
      {accountsStats && (
        <>
          <div>
            <p className="mb-1 text-xs font-medium text-slate-600">Working / Suspended</p>
            <VerticalStackedBar
              barHeight="h-16"
              total={accountsStats.total}
              segments={[
                { key: 'working', count: accountsStats.by_working_state.working, color: '#22c55e' },
                { key: 'suspended', count: accountsStats.by_working_state.suspended, color: '#ef4444' },
              ]}
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-slate-600">Login state</p>
            <VerticalStackedBar
              barHeight="h-56"
              total={accountsStats.total}
              hideZero
              segments={loginStateSegments}
            />
          </div>
        </>
      )}

      {providersStats && <ProviderStatsByServing providersStats={providersStats} />}
    </div>
  );
}

// 6 ô tổng (tổng tiền mua + tổng request x3 service_type + tổng request
// chung + avg cost) của đúng tập account đang filter hiện tại - xem
// manager.get_accounts_cost_stats()'s docstring.
function AccountsCostStatsBar({ costStats }) {
  if (!costStats) return null;
  const cells = [
    { label: 'Tổng tiền mua', value: formatVnd(costStats.total_vnd) },
    { label: 'Request FB QL', value: formatNumber(costStats.total_request_by_service_type.fb_ql) },
    { label: 'Request EAAAAU', value: formatNumber(costStats.total_request_by_service_type.fb_eaaaau) },
    { label: 'Request EAAGNO', value: formatNumber(costStats.total_request_by_service_type.fb_eaagno) },
    { label: 'Tổng request', value: formatNumber(costStats.total_request) },
    { label: 'Avg cost / request', value: formatVnd(costStats.avg_cost_vnd) },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 rounded-xl border border-cyan-100 bg-white p-3 sm:grid-cols-3 md:grid-cols-6">
      {cells.map((c) => (
        <div key={c.label} className="rounded-lg bg-slate-50 p-2 text-center">
          <p className="text-[11px] text-slate-500">{c.label}</p>
          <p className="text-sm font-semibold text-slate-800">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function ProviderCard({ label, provider, onTest, onReset, busyKey }) {
  if (!provider) {
    return (
      <div className="rounded-xl border border-slate-200 p-3 text-sm text-slate-400">
        {label}: không có provider
      </div>
    );
  }
  const isBusy = busyKey === provider.id;
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-900">
          <LiveDot isLive={provider.is_live} />
          {label}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={isBusy}
            onClick={() => onTest(provider.id)}
            className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40"
          >
            Test
          </button>
          <button
            type="button"
            disabled={isBusy}
            onClick={() => onReset(provider.id)}
            className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40"
          >
            Reset
          </button>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-1 text-xs text-slate-600">
        <dt className="text-slate-400">Last live</dt>
        <dd>{formatDateTime(provider.last_live_at)}</dd>
        <dt className="text-slate-400">Last die</dt>
        <dd>{formatDateTime(provider.last_die_at)}</dd>
        <dt className="text-slate-400">Next request</dt>
        <dd>{formatDateTime(provider.next_request_at)}</dd>
        <dt className="text-slate-400">Request count</dt>
        <dd>{provider.request_counter}</dd>
        <dt className="text-slate-400">Need relogin</dt>
        <dd>{provider.need_account_relogin ? 'Có' : 'Không'}</dd>
      </dl>
      {provider.last_reason && (
        <p className="mt-2 truncate text-xs text-slate-500" title={provider.last_reason}>
          {provider.last_reason}
        </p>
      )}
    </div>
  );
}

function AccountDetailDialog({ uid, onClose }) {
  const [account, setAccount] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [busyProviderId, setBusyProviderId] = useState(null);

  const load = useCallback(() => {
    getAccount(uid).then(setAccount).catch((error) => toast.error(getErrorMessage(error)));
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleTest(providerId) {
    setBusyProviderId(providerId);
    try {
      await testProvider(providerId);
      toast.success('Test xong.');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyProviderId(null);
    }
  }

  async function handleReset(providerId) {
    setBusyProviderId(providerId);
    try {
      await resetProvider(providerId);
      toast.success('Reset xong.');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyProviderId(null);
    }
  }

  if (!account) {
    return (
      <DialogShell onClose={onClose}>
        <p className="text-sm text-slate-500">Đang tải...</p>
      </DialogShell>
    );
  }

  const sensitiveFields = ['cookie', 'password', 'email_password', 'code_2fa', 'proxy_password'];

  return (
    <DialogShell onClose={onClose}>
      <div className="max-h-[80vh] w-[32rem] max-w-full overflow-y-auto">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">{account.uid}</h3>
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className="text-xs font-medium text-cyan-700 hover:underline"
          >
            {revealed ? 'Ẩn thông tin nhạy cảm' : 'Hiện thông tin nhạy cảm'}
          </button>
        </div>

        <dl className="mb-4 grid grid-cols-2 gap-1 text-xs text-slate-600">
          <dt className="text-slate-400">Login state</dt>
          <dd><LoginStateBadge value={account.login_state} /></dd>
          <dt className="text-slate-400">Working state</dt>
          <dd><WorkingStateBadge value={account.working_state} /></dd>
          <dt className="text-slate-400">Added at</dt>
          <dd>{formatDateTime(account.added_at)}</dd>
          <dt className="text-slate-400">Label</dt>
          <dd>{account.label || '—'}</dd>
          <dt className="text-slate-400">Note</dt>
          <dd>{account.note || '—'}</dd>
          {sensitiveFields.map((field) => (
            <>
              <dt key={`${field}-k`} className="text-slate-400">{field}</dt>
              <dd key={`${field}-v`} className="truncate">
                {revealed ? account[field] || '—' : account[field] ? '•••••' : '—'}
              </dd>
            </>
          ))}
        </dl>

        <div className="space-y-2">
          <ProviderCard label="GraphQL" provider={account.graph_ql} onTest={handleTest} onReset={handleReset} busyKey={busyProviderId} />
          <ProviderCard label="EAAAAU" provider={account.graph_eaaaau} onTest={handleTest} onReset={handleReset} busyKey={busyProviderId} />
          <ProviderCard label="EAAGNO" provider={account.graph_eaagno} onTest={handleTest} onReset={handleReset} busyKey={busyProviderId} />
        </div>
      </div>
    </DialogShell>
  );
}

function AddAccountsDialog({ onClose, onDone }) {
  const [website, setWebsite] = useState('');
  const [productName, setProductName] = useState('');
  const [unitVnd, setUnitVnd] = useState('');
  const [dataText, setDataText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const data = await addAccounts({ website, productName, unitVnd: Number(unitVnd), dataText });
      setResult(data);
      toast.success(`Đã thêm ${data.total} account.`);
      onDone();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <div className="w-[28rem] max-w-full">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Thêm accounts (combo-lines)</h3>
        <p className="mb-3 text-xs text-slate-500">
          Dùng khi đã có sẵn combo-lines text (vd vừa mua tay ngoài hệ thống). Nếu muốn hệ thống tự mua qua
          marketplace API, dùng nút "Mua ngay" ở tab Purchases.
        </p>
        {result ? (
          <div className="space-y-2 text-sm">
            <p>Đã thêm {result.total} account, tổng {formatVnd(result.total_vnd)}.</p>
            <button type="button" onClick={onClose} className="rounded-lg bg-cyan-600 px-3 py-2 text-sm text-white">
              Đóng
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2">
            <input
              required
              placeholder="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <input
              required
              placeholder="product_name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <input
              required
              type="number"
              placeholder="unit_vnd"
              value={unitVnd}
              onChange={(e) => setUnitVnd(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <textarea
              required
              rows={8}
              placeholder="Raw combo-lines, mỗi dòng 1 account"
              value={dataText}
              onChange={(e) => setDataText(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Đang thêm...' : 'Thêm accounts'}
            </button>
          </form>
        )}
      </div>
    </DialogShell>
  );
}

function BulkDeleteDialog({ deletingToken, count, accountsStats, providersStats, onClose, onDone }) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  async function handleCancel() {
    setIsCancelling(true);
    try {
      await cancelDeletingRequest(deletingToken);
    } catch (error) {
      // Lỗi mạng lúc cancel không sao - request kẹt ở RAM backend sẽ tự
      // hết hạn (DELETE_REQUEST_TIMEOUT_SECONDS), không có gì bị xoá vì
      // chưa confirm. Vẫn đóng dialog bình thường.
    } finally {
      setIsCancelling(false);
      onClose();
    }
  }

  async function handleConfirm() {
    setIsConfirming(true);
    try {
      const data = await confirmDeletingRequest(deletingToken);
      toast.success(`Đã xoá ${data.count} account.`);
      onDone();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsConfirming(false);
    }
  }

  return (
    <DialogShell onClose={handleCancel}>
      <div className="max-h-[80vh] w-[70vw] max-w-full overflow-y-auto">
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Xác nhận xoá accounts</h3>
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Sẽ xoá <span className="font-bold">{count}</span> account khớp bộ lọc hiện tại. Hành động không thể hoàn tác.
        </div>

        <AccountStatsBars accountsStats={accountsStats} providersStats={providersStats} />

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isCancelling || isConfirming}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Huỷ
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isCancelling || isConfirming}
            className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isConfirming ? 'Đang xoá...' : 'Xác nhận XOÁ'}
          </button>
        </div>
      </div>
    </DialogShell>
  );
}

export default function AccountsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageStr, setPageStr] = useUrlState('accounts', 'page', '1');
  const page = Number(pageStr);
  const setPage = useCallback(
    (next) => setPageStr((prev) => String(typeof next === 'function' ? next(Number(prev)) : next)),
    [setPageStr]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [accountsStats, setAccountsStats] = useState(null);
  const [providersStats, setProvidersStats] = useState(null);
  const [accountsCostStats, setAccountsCostStats] = useState(null);

  // appliedFilters/sortBy/sortAsc/page persist lên URL - F5/share link giữ
  // đúng bộ lọc đang xem. filterDraft (giá trị đang gõ/chọn, chưa bấm "Lọc")
  // thì KHÔNG persist, chỉ khởi tạo 1 lần từ appliedFilters lúc mount.
  const [loginState] = useUrlState('accounts', 'login_state', '');
  const [workingState] = useUrlState('accounts', 'working_state', '');
  const [serviceType] = useUrlState('accounts', 'service_type', '');
  const [specificService] = useUrlState('accounts', 'specific_service', '');
  const [addedAtPrefix] = useUrlState('accounts', 'added_at_prefix', '');
  const [uid] = useUrlState('accounts', 'uid', '');
  const appliedFilters = {
    login_state: loginState,
    working_state: workingState,
    service_type: serviceType,
    specific_service: specificService,
    added_at_prefix: addedAtPrefix,
    uid,
  };
  // Apply filter + reset page về 1 trong đúng 1 lần cập nhật URL - xem
  // useUrlStateBatch.
  const setUrlParams = useUrlStateBatch('accounts');
  function applyFiltersAndResetPage(next) {
    setUrlParams({ ...next, page: 1 }, { page: '1' });
  }
  const [filterDraft, setFilterDraft] = useState(appliedFilters);
  const [sortBy, setSortBy] = useUrlState('accounts', 'sort_by', 'added_at');
  const [sortAscStr, setSortAscStr] = useUrlState('accounts', 'sort_asc', 'false');
  const sortAsc = sortAscStr === 'true';
  const setSortAsc = (next) => setSortAscStr(String(typeof next === 'function' ? next(sortAsc) : next));
  const [selectedUid, setSelectedUid] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [bulkDeleteRequest, setBulkDeleteRequest] = useState(null);
  const [isCreatingDeleteRequest, setIsCreatingDeleteRequest] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        sort_by: sortBy,
        sort_asc: sortAsc,
      };
      if (appliedFilters.login_state) params.login_state = appliedFilters.login_state;
      if (appliedFilters.working_state) params.working_state = appliedFilters.working_state;
      if (appliedFilters.service_type) params.service_type = appliedFilters.service_type;
      // specific_service (URL) gộp chung 1 lựa chọn: '' = không lọc, 'all' =
      // chỉ account serve tất cả (only_serving_all=true), 'fb_ql'/... = chỉ
      // account đang cô lập đúng service đó (specific_service=X) - tách
      // thành 2 param khi gọi API vì backend cần phân biệt rõ 2 ý nghĩa
      // (xem clone_manager/accounts.py's list_accounts() docstring).
      if (appliedFilters.specific_service === 'all') params.only_serving_all = true;
      else if (appliedFilters.specific_service) params.specific_service = appliedFilters.specific_service;
      if (appliedFilters.added_at_prefix) params.added_at_prefix = appliedFilters.added_at_prefix;
      if (appliedFilters.uid) params.uid = appliedFilters.uid;

      const data = await listAccounts(params);
      setItems(data.items);
      setTotal(data.paging.total_item);
      setAccountsStats(data.accounts_stats);
      setProvidersStats(data.providers_stats);
      setAccountsCostStats(data.accounts_cost_stats);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [page, sortBy, sortAsc, loginState, workingState, serviceType, specificService, addedAtPrefix, uid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function handleApplyFilter() {
    applyFiltersAndResetPage(filterDraft);
  }

  async function handleDelete(uid) {
    if (!window.confirm(`Xoá account ${uid}? Hành động không thể hoàn tác.`)) return;
    try {
      await deleteAccount(uid);
      toast.success('Đã xoá.');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  async function handleCheckAndDelete() {
    setIsCreatingDeleteRequest(true);
    try {
      const params = {};
      if (filterDraft.login_state) params.login_state = filterDraft.login_state;
      if (filterDraft.working_state) params.working_state = filterDraft.working_state;
      if (filterDraft.service_type) params.service_type = filterDraft.service_type;
      if (filterDraft.added_at_prefix) params.added_at_prefix = filterDraft.added_at_prefix;
      if (filterDraft.uid) params.uid = filterDraft.uid;

      const data = await createDeletingRequest(params);
      setBulkDeleteRequest(data);
      // Đồng bộ luôn appliedFilters + page để fetchData() sau khi xoá
      // (BulkDeleteDialog's onDone) refresh đúng theo filter vừa dùng để
      // xoá, không phải filter cũ trước đó.
      applyFiltersAndResetPage(filterDraft);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreatingDeleteRequest(false);
    }
  }

  const columns = [
    { key: 'uid', label: 'UID', sortable: true },
    { key: 'label', label: 'Label', render: (row) => row.label || '—' },
    { key: 'login_state', label: 'Login state', sortable: true, render: (row) => <LoginStateBadge value={row.login_state} /> },
    { key: 'added_at', label: 'Added at', sortable: true, render: (row) => formatDateTime(row.added_at) },
    {
      key: 'proxy',
      label: 'Proxy',
      render: (row) =>
        row.proxy_host ? (
          <span className="font-mono text-xs">{row.proxy_host}:{row.proxy_port}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'providers',
      label: 'Providers',
      render: (row) => (
        <div className="flex gap-1.5">
          <LiveDot isLive={row.graph_ql?.is_live} title={`GraphQL: ${row.graph_ql?.last_reason || ''}`} />
          <LiveDot isLive={row.graph_eaaaau?.is_live} title={`EAAAAU: ${row.graph_eaaaau?.last_reason || ''}`} />
          <LiveDot isLive={row.graph_eaagno?.is_live} title={`EAAGNO: ${row.graph_eaagno?.last_reason || ''}`} />
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex gap-2">
          <button type="button" onClick={() => setSelectedUid(row.uid)} className="text-xs font-medium text-cyan-700 hover:underline">
            Chi tiết
          </button>
          <button type="button" onClick={() => handleDelete(row.uid)} className="text-xs font-medium text-red-600 hover:underline">
            Xoá
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-cyan-100 bg-white p-3">
        <select
          value={filterDraft.login_state}
          onChange={(e) => setFilterDraft((f) => ({ ...f, login_state: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Login state: Tất cả</option>
          {LOGIN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filterDraft.working_state}
          onChange={(e) => setFilterDraft((f) => ({ ...f, working_state: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Working state: Tất cả</option>
          <option value="working">working</option>
          <option value="suspended">suspended</option>
        </select>
        <select
          value={filterDraft.service_type}
          onChange={(e) => setFilterDraft((f) => ({ ...f, service_type: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Service type: Tất cả</option>
          {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filterDraft.specific_service}
          onChange={(e) => setFilterDraft((f) => ({ ...f, specific_service: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Specific service: Tất cả</option>
          <option value="all">Chỉ serve All (chưa cô lập)</option>
          {SERVICE_TYPES.map((s) => (
            <option key={s} value={s}>Chỉ Only {SERVICE_TYPE_LABEL[s]}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          UID
          <input
            type="text"
            placeholder="account uid"
            value={filterDraft.uid}
            onChange={(e) => setFilterDraft((f) => ({ ...f, uid: e.target.value.trim() }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
            className="w-44 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Added at
          <input
            type="text"
            placeholder="2026-10-05 07:50:17"
            value={filterDraft.added_at_prefix}
            onChange={(e) => setFilterDraft((f) => ({ ...f, added_at_prefix: e.target.value }))}
            className="w-48 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <button
          type="button"
          onClick={handleApplyFilter}
          className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          Lọc
        </button>
        <button
          type="button"
          onClick={handleCheckAndDelete}
          disabled={isCreatingDeleteRequest}
          className="ml-auto rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          {isCreatingDeleteRequest ? 'Đang kiểm tra...' : 'Kiểm tra và Xoá'}
        </button>
        <button
          type="button"
          onClick={() => setShowAddDialog(true)}
          className="rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-700"
        >
          + Thêm accounts
        </button>
      </div>

      <AccountStatsBars accountsStats={accountsStats} providersStats={providersStats} />
      <AccountsCostStatsBar costStats={accountsCostStats} />

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDir={sortAsc ? 'asc' : 'desc'}
        onSortChange={(key, dir) => { setSortBy(key); setSortAsc(dir === 'asc'); }}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {selectedUid && <AccountDetailDialog uid={selectedUid} onClose={() => setSelectedUid(null)} />}
      {showAddDialog && (
        <AddAccountsDialog
          onClose={() => setShowAddDialog(false)}
          onDone={fetchData}
        />
      )}
      {bulkDeleteRequest && (
        <BulkDeleteDialog
          deletingToken={bulkDeleteRequest.deleting_token}
          count={bulkDeleteRequest.count}
          accountsStats={bulkDeleteRequest.accounts_stats}
          providersStats={bulkDeleteRequest.providers_stats}
          onClose={() => setBulkDeleteRequest(null)}
          onDone={() => {
            setBulkDeleteRequest(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
